import { z } from 'zod';
import { http, API_BASE, getAuthToken } from '@/services/http';
import {
  buildHexisChatBody, buildHexisApproveBody, createHexisChatStream,
  type HexisReply, type HexisApproveResult,
} from '@marcianus/shared/hexis-client';

const pendingActionSchema = z.object({
  token: z.string(),
  tool: z.string(),
  summary: z.string().nullish().transform((s) => s ?? ''),
}).nullish();
const chatSchema = z.object({
  reply: z.string().nullish(),
  suggestions: z.array(z.string()).nullish(),
  intent: z.string().nullish(),
  pending_action: pendingActionSchema,
}).passthrough();
export type { HexisReply };

/** Copiloto Hexis → POST /hexis/chat (Legacy non-stream) */
export async function askHexis(params: {
  message: string;
  userId: string;
  eventId?: string;
  context?: { tab: string; subtab: string; label: string };
}): Promise<HexisReply> {
  const raw = await http.post<unknown>('/hexis/chat', buildHexisChatBody({
    message: params.message,
    userId: params.userId,
    eventId: params.eventId,
    context: params.context,
  }));
  return chatSchema.parse(raw ?? {});
}

// Chunk normalizado que a HexisPage consome (Chain of Thought + resposta).
export type HexisStreamChunk =
  | { type: 'think' | 'tool'; content: string; name?: string }
  | { type: 'reply'; content: string };

/**
 * Copiloto Hexis em streaming → POST /hexis/ai/chat (SSE).
 *
 * Este é o endpoint REAL do cérebro (registado em /api/v1/hexis/ai/chat, guardado
 * por optionalAuth). O servidor emite eventos `{type:'token'|'step'|'done'|'error'}`;
 * aqui traduzimo-los para o contrato think/tool/reply que a UI já entende — a
 * tradução mora num sítio só, e a página não precisa de saber o formato do fio.
 *
 * Correções face à versão anterior (que apontava para o vazio):
 *   • URL: API_BASE (/api/v1) + /hexis/ai/chat — antes ia para /hexis/chat/stream
 *     (inexistente) na origem estática da Hostinger.
 *   • Auth: token de sessão Clerk (getAuthToken) — antes lia um marc:access_token
 *     do localStorage que o cutover extinguiu.
 *   • Contrato: mapeia token/step/done — antes esperava reply/think/tool que o
 *     servidor nunca envia.
 */
export async function* askHexisStream(params: {
  message: string;
  userId: string;
  eventId?: string;
  context?: { tab: string; subtab: string; label: string };
}): AsyncGenerator<HexisStreamChunk, void, unknown> {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE}/hexis/ai/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      message: params.message,
      currentPage: params.context?.label || '/hexis',
    }),
  });

  if (!response.ok || !response.body) {
    throw new Error('Falha ao iniciar stream do Hexis');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const frames = buffer.split('\n\n');
    buffer = frames.pop() || ''; // o último frame pode estar incompleto

    for (const frame of frames) {
      // Linhas de comentário SSE (': hb' keepalive) são ignoradas.
      const dataLine = frame.split('\n').find((l) => l.startsWith('data: '));
      if (!dataLine) continue;
      const dataStr = dataLine.slice(6).trim();
      if (!dataStr || dataStr === '[DONE]') continue;

      let ev: { type?: string; token?: string; label?: string; response?: string; message?: string };
      try { ev = JSON.parse(dataStr); } catch { continue; }

      if (ev.type === 'token' && ev.token) {
        yield { type: 'reply', content: ev.token };
      } else if (ev.type === 'step' && ev.label) {
        yield { type: 'think', content: ev.label };
      } else if (ev.type === 'done') {
        return;   // os tokens já compuseram a resposta; nada a acrescentar
      } else if (ev.type === 'error') {
        throw new Error(ev.message || 'Erro no stream do Hexis');
      }
    }
  }
}

/** Aprova uma ação proposta pelo Hexis (Human-in-the-Loop). */
export async function approveHexisAction(params: { token: string; userId: string }): Promise<HexisApproveResult> {
  return http.post<HexisApproveResult>('/hexis/tool/approve',
    buildHexisApproveBody(params.token, params.userId));
}

/**
 * O MESMO `/hexis/chat`, com progresso: os passos reais do cérebro chegam
 * enquanto pensa e o payload final é idêntico ao de `askHexis` — por isso o
 * function calling (pending_action) e as sugestões continuam todos cá.
 *
 * O `open` injeta a base e o token da app; o parser vive no shared, partilhado
 * com a consola HQ.
 */
export const askHexisComProgresso = createHexisChatStream(async (path, body) => {
  const token = await getAuthToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  return { ok: res.ok, body: res.body };
});
